import { Menu } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteNavigationProgress } from '../../../components/navigation/RouteNavigationProgress';
import { ThemeToggle } from '../../../forge/components/layout/ThemeToggle';
import { useForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { useMobileNavDrawer } from '../../../forge/hooks/useMobileNavDrawer';
import { useSidebar } from '../../../forge/hooks/useSidebar';
import '../../../forge/styles/forge-theme.css';
import { Toaster } from '../../../ui/sonner';
import { UiProvider } from '../../../ui/ui-provider';
import '../../styles/archon-theme.css';
import { getArchonPageLabel } from './archonNavigation';
import { ArchonSidebar } from './ArchonSidebar';

export const ArchonLayout: React.FC = () => {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const { theme, toggleTheme } = useForgeTheme();
  const { width, isCollapsed, isHidden, isResizing, hideSidebar, showSidebar, onDragStart } =
    useSidebar();
  const location = useLocation();
  const {
    isMobile,
    isOpen: isMobileSidebarOpen,
    open: openMobileSidebar,
    close: closeMobileSidebar,
    openerRef: mobileNavButtonRef,
    drawerRef: mobileSidebarShellRef,
  } = useMobileNavDrawer(location.pathname);
  const pageLabel = useMemo(() => getArchonPageLabel(location.pathname), [location.pathname]);
  // Hiding takes the sidebar off the page rather than collapsing it to an icon rail, the
  // way Forge does, and leaves the same small handle at the bottom-left to bring it back.
  // The mobile drawer is unaffected: it is opened from the top bar, not from this state.
  const isSidebarHidden = isHidden && !isMobile;

  return (
    <div className="forge-root forge-shell" data-forge-theme={theme}>
      <div
        ref={setPortalContainer}
        data-testid="archon-root"
        className={`forge-layout archon-root ${isMobile ? 'is-mobile-shell' : ''}`}
      >
        <UiProvider container={portalContainer}>
          <RouteNavigationProgress />
          {isMobile ? (
            <div className="forge-mobile-topbar">
              <button
                type="button"
                ref={mobileNavButtonRef}
                className="forge-mobile-nav-button"
                aria-label="Open navigation"
                aria-expanded={isMobileSidebarOpen}
                onClick={openMobileSidebar}
              >
                <Menu aria-hidden="true" />
              </button>
              <div className="forge-mobile-title">{pageLabel}</div>
              <ThemeToggle theme={theme} onToggle={toggleTheme} variant="topbar" />
            </div>
          ) : null}
          {isSidebarHidden ? null : (
            <div
              ref={mobileSidebarShellRef}
              tabIndex={-1}
              className={`forge-sidebar-shell ${isMobileSidebarOpen ? 'is-mobile-open' : ''}`}
            >
              {isMobile ? (
                <button
                  type="button"
                  className="forge-sidebar-backdrop"
                  aria-label="Close navigation"
                  onClick={closeMobileSidebar}
                />
              ) : null}
              <ArchonSidebar
                theme={theme}
                width={width}
                isCollapsed={isCollapsed}
                isResizing={isResizing}
                hideSidebar={hideSidebar}
                onDragStart={onDragStart}
                toggleTheme={toggleTheme}
                currentPath={location.pathname}
                isMobile={isMobile}
                onMobileNavigate={isMobile ? closeMobileSidebar : undefined}
              />
            </div>
          )}
          {isSidebarHidden ? (
            <button
              type="button"
              className="forge-sidebar-reveal-button"
              aria-label="Show navigation"
              title="Show navigation"
              onClick={showSidebar}
            >
              <Menu aria-hidden="true" />
            </button>
          ) : null}
          <main
            className={`forge-main-content ${isResizing ? 'sidebar-resizing' : ''}`}
            style={{ marginLeft: isMobile || isSidebarHidden ? 0 : isCollapsed ? 38 : width }}
          >
            <Outlet />
          </main>
          <Toaster position="bottom-right" />
        </UiProvider>
      </div>
    </div>
  );
};
